import { calculateDistance } from "../calculateDistance";
import { getElevationStatistics } from "./getElevationStatistics";

export const getBaseStatistics = (points) => {
  if (!points || points.length < 2) {
    // Need at least 2 points for meaningful stats
    return {
      points: {
        count: points ? points.length : 0,
      },
      distance: {
        total: null,
      },
      time: {
        start: null,
        end: null,
      },
      duration: {
        total: null,
      },
      speed: {
        average: null,
      },
      pace: {
        average: null,
      },
      heartrate: {
        start: null,
        end: null,
        min: null,
        max: null,
        average: null,
      },
      temperature: {
        start: null,
        end: null,
        min: null,
        max: null,
        average: null,
      },
      cadence: {
        min: null,
        max: null,
        average: null,
      },
      elevation: {
        ascent: null,
        descent: null,
      },
      spatial: {
        boundaries: {
          minLat: null,
          maxLat: null,
          minLng: null,
          maxLng: null,
        },
        center: {
          lat: null,
          lng: null,
        },
        startCoordinate: {
          lat: null,
          lng: null,
        },
        endCoordinate: {
          lat: null,
          lng: null,
        },
      },
    };
  }

  let totalDistance = 0;
  let minHeartrate = Infinity;
  let maxHeartrate = -Infinity;
  let sumHeartrate = 0;
  let countHeartrate = 0;
  let minTemperature = Infinity;
  let maxTemperature = -Infinity;
  let sumTemperature = 0;
  let countTemperature = 0;
  let minCadence = Infinity;
  let maxCadence = -Infinity;
  let sumCadence = 0;
  let countCadence = 0;
  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;
  let sumLat = 0;
  let sumLng = 0;

  const firstPoint = points[0];
  const lastPoint = points[points.length - 1];

  const startTime = firstPoint.time ? new Date(firstPoint.time) : null;
  const endTime = lastPoint.time ? new Date(lastPoint.time) : null;
  const durationMs =
    startTime && endTime ? endTime.getTime() - startTime.getTime() : 0;

  if (firstPoint.heartrate !== null) {
    minHeartrate = firstPoint.heartrate;
    maxHeartrate = firstPoint.heartrate;
    sumHeartrate = firstPoint.heartrate;
    countHeartrate = 1;
  }
  if (firstPoint.temperature !== null) {
    minTemperature = firstPoint.temperature;
    maxTemperature = firstPoint.temperature;
    sumTemperature = firstPoint.temperature;
    countTemperature = 1;
  }
  if (firstPoint.cadence !== null) {
    minCadence = firstPoint.cadence;
    maxCadence = firstPoint.cadence;
    sumCadence = firstPoint.cadence;
    countCadence = 1;
  }

  minLat = firstPoint.lat;
  maxLat = firstPoint.lat;
  minLng = firstPoint.lng;
  maxLng = firstPoint.lng;
  sumLat = firstPoint.lat;
  sumLng = firstPoint.lng;

  for (let i = 1; i < points.length; i++) {
    const prevPoint = points[i - 1];
    const currentPoint = points[i];

    // Distance
    totalDistance += calculateDistance(
      prevPoint.lat,
      prevPoint.lng,
      currentPoint.lat,
      currentPoint.lng
    );

    // Heartrate
    if (currentPoint.heartrate !== null) {
      minHeartrate = Math.min(minHeartrate, currentPoint.heartrate);
      maxHeartrate = Math.max(maxHeartrate, currentPoint.heartrate);
      sumHeartrate += currentPoint.heartrate;
      countHeartrate++;
    }

    // Temperature
    if (currentPoint.temperature !== null) {
      minTemperature = Math.min(minTemperature, currentPoint.temperature);
      maxTemperature = Math.max(maxTemperature, currentPoint.temperature);
      sumTemperature += currentPoint.temperature;
      countTemperature++;
    }

    // Cadence
    if (currentPoint.cadence !== null) {
      minCadence = Math.min(minCadence, currentPoint.cadence);
      maxCadence = Math.max(maxCadence, currentPoint.cadence);
      sumCadence += currentPoint.cadence;
      countCadence++;
    }

    // Elevation calculation removed from loop to correctly use logic in getElevationStatistics

    // Spatial Boundaries and Center
    minLat = Math.min(minLat, currentPoint.lat);
    maxLat = Math.max(maxLat, currentPoint.lat);
    minLng = Math.min(minLng, currentPoint.lng);
    maxLng = Math.max(maxLng, currentPoint.lng);
    sumLat += currentPoint.lat;
    sumLng += currentPoint.lng;
  }

  const avgHeartrate =
    countHeartrate > 0 ? sumHeartrate / countHeartrate : null;
  const avgTemperature =
    countTemperature > 0 ? sumTemperature / countTemperature : null;
  const avgCadence = countCadence > 0 ? sumCadence / countCadence : null;

  const centerLat = sumLat / points.length;
  const centerLng = sumLng / points.length;

  // Calculate average speed and pace
  const totalDurationSeconds = durationMs / 1000;
  const averageSpeed =
    totalDurationSeconds > 0 && totalDistance > 0
      ? parseFloat(((totalDistance / totalDurationSeconds) * 3.6).toFixed(1)) // km/h
      : null;
  const averagePace =
    totalDistance > 0 && totalDurationSeconds > 0
      ? parseFloat(
          (totalDurationSeconds / 60 / (totalDistance / 1000)).toFixed(1)
        ) // min/km
      : null;

  const distanceKm = parseInt(totalDistance / 1000);
  const eleStats = getElevationStatistics(points, distanceKm);

  return {
    statistics: {
      distance: {
        total: Math.round(totalDistance), // meters, rounded to nearest whole number
      },
      time: {
        start: startTime ? startTime.toISOString() : null,
        end: endTime ? endTime.toISOString() : null,
      },
      duration: {
        total: Math.round(totalDurationSeconds), // in seconds, rounded to nearest whole number
      },
      speed: {
        average: averageSpeed,
      },
      pace: {
        average: averagePace,
      },
      heartrate: {
        start: firstPoint.heartrate,
        end: lastPoint.heartrate,
        min: minHeartrate === Infinity ? null : Math.round(minHeartrate),
        max: maxHeartrate === -Infinity ? null : Math.round(maxHeartrate),
        average: avgHeartrate !== null ? Math.round(avgHeartrate) : null,
      },
      temperature: {
        start: firstPoint.temperature,
        end: lastPoint.temperature,
        min:
          minTemperature === Infinity
            ? null
            : parseFloat(minTemperature.toFixed(1)), // Temp usually has 1 decimal
        max:
          maxTemperature === -Infinity
            ? null
            : parseFloat(maxTemperature.toFixed(1)),
        average:
          avgTemperature !== null
            ? parseFloat(avgTemperature.toFixed(1))
            : null,
      },
      cadence: {
        min: minCadence === Infinity ? null : Math.round(minCadence),
        max: maxCadence === -Infinity ? null : Math.round(maxCadence),
        average: avgCadence !== null ? Math.round(avgCadence) : null,
      },
      elevation: {
        ascent: eleStats.ascent,
        descent: eleStats.descent,
      },
    },
    spatial: {
      boundaries: {
        minLat: parseFloat(minLat.toFixed(6)),
        maxLat: parseFloat(maxLat.toFixed(6)),
        minLng: parseFloat(minLng.toFixed(6)),
        maxLng: parseFloat(maxLng.toFixed(6)),
      },
      center: {
        lat: parseFloat(centerLat.toFixed(6)),
        lng: parseFloat(centerLng.toFixed(6)),
      },
      startCoordinate: {
        lat: firstPoint.lat,
        lng: firstPoint.lng,
      },
      endCoordinate: {
        lat: lastPoint.lat,
        lng: lastPoint.lng,
      },
    },
  };
};
